/**
 * Backlog KPI w układzie fridayowym: MIESIĄC → TYDZIEŃ → zadanie → podzadania.
 *
 * Nagłówki miesięcy i tygodni NIE są danymi — są wyliczane z terminu zadania (`week_start`,
 * czyli poniedziałek jego tygodnia). Na tablicach Friday te same nagłówki są wpisywane
 * ręcznie jako podzadania i rozjechały się do 62 pisowni; tutaj nie da się ich rozjechać,
 * bo kluczem jest data.
 *
 * Własna szuflada („Sprawy bieżące — JK") to po prostu zadanie, które ma podzadania —
 * dokładnie tak, jak zespoły robią to dziś ręcznie we Friday.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Bell, Check, ChevronDown, ChevronRight, GripVertical, MessageSquare, Pencil, Plus, Trash2 } from "lucide-react";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { PRIORITY_COLORS, PRIORITY_LABELS } from "@/features/tasks/types";
import { Button } from "@/components/ui/Button";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { WeekSelect } from "@/components/ui/WeekSelect";
import { useAuthStore } from "@/store/auth";
import { useInternalUsers } from "@/features/tasks/hooks/useInternalUsers";
import {
  useKpiActionItemMutations,
  useKpiGroupStates,
  useKpiItemCommentMutations,
  useKpiItemComments,
} from "../hooks";
import type { KpiActionItem, KpiActionPriority } from "../types";
import { canPingKpi, fmtDate, fmtDateTime, isDirectorateKpiItem, kpiOriginHint } from "../utils";
import { formatDate } from "@/utils/formatDate";
import { KPI_ACTION_LABELS, KPI_ACTION_ORDER, STATUS_CELL, isKpiUnfinished } from "./ui";
import { CommentBody } from "./CommentBody";
import { KpiItemDetailPanel } from "./KpiItemDetailPanel";

// ── klucze i etykiety grup ───────────────────────────────────────────────────────
const MONTHS = [
  "STYCZEŃ", "LUTY", "MARZEC", "KWIECIEŃ", "MAJ", "CZERWIEC",
  "LIPIEC", "SIERPIEŃ", "WRZESIEŃ", "PAŹDZIERNIK", "LISTOPAD", "GRUDZIEŃ",
];

/** Poniedziałek bieżącego tygodnia w czasie lokalnym (nie UTC — inaczej w nocy przeskakuje). */
export function currentMonday(): string {
  const today = new Date();
  const day = today.getDay() || 7;
  const mon = new Date(today);
  mon.setDate(today.getDate() - (day - 1));
  return iso(mon);
}

function iso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function parseIso(value: string): Date {
  const [y, m, d] = value.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function monthKeyOf(weekStart: string): string {
  return weekStart.slice(0, 7);
}

export function monthLabel(monthKey: string): string {
  const [y, m] = monthKey.split("-").map(Number);
  return `${MONTHS[m - 1]} ${y}`;
}

/** „TYDZIEŃ 2 · 10–16.08" — numer tygodnia liczony jako który to poniedziałek w miesiącu. */
export function weekLabel(weekStart: string): string {
  const monday = parseIso(weekStart);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const nth = Math.floor((monday.getDate() - 1) / 7) + 1;
  const pad = (n: number) => String(n).padStart(2, "0");
  const range = monday.getMonth() === sunday.getMonth()
    ? `${pad(monday.getDate())}–${pad(sunday.getDate())}.${pad(monday.getMonth() + 1)}`
    : `${pad(monday.getDate())}.${pad(monday.getMonth() + 1)}–${pad(sunday.getDate())}.${pad(sunday.getMonth() + 1)}`;
  return `TYDZIEŃ ${nth} · ${range}`;
}

// ── budowa drzewa i kubełków ─────────────────────────────────────────────────────
export interface WeekGroup { key: string; label: string; roots: KpiActionItem[] }
export interface MonthGroup { key: string; label: string; weeks: WeekGroup[]; total: number; done: number }

interface Buckets {
  overdue: KpiActionItem[];
  months: MonthGroup[];
  noDate: KpiActionItem[];
  doneNoDate: KpiActionItem[];
  childrenOf: Map<string, KpiActionItem[]>;
}

/**
 * Grupujemy TYLKO korzenie — podzadania idą pod swojego rodzica niezależnie od własnego
 * terminu, bo tak czyta się drzewo (podzadanie „Korekta PB" bez kontekstu szuflady nic
 * nie znaczy). Kubełek „PO TERMINIE" bierze wyłącznie nieskończone: zadanie odhaczone
 * po terminie jest zrobione, nie zaległe.
 */
export function buildBuckets(items: KpiActionItem[], today = currentMonday()): Buckets {
  const childrenOf = new Map<string, KpiActionItem[]>();
  const roots: KpiActionItem[] = [];
  const byId = new Set(items.map((i) => i.id));
  for (const item of items) {
    // Sierota (rodzic poza zestawem) traktowana jak korzeń — inaczej zniknęłaby z widoku.
    if (item.parent && byId.has(item.parent)) {
      const list = childrenOf.get(item.parent) ?? [];
      list.push(item);
      childrenOf.set(item.parent, list);
    } else {
      roots.push(item);
    }
  }
  const byOrder = (a: KpiActionItem, b: KpiActionItem) =>
    a.order - b.order || a.created_at.localeCompare(b.created_at);
  for (const list of childrenOf.values()) list.sort(byOrder);
  roots.sort(byOrder);

  const overdue: KpiActionItem[] = [];
  const noDate: KpiActionItem[] = [];
  const doneNoDate: KpiActionItem[] = [];
  const weeks = new Map<string, KpiActionItem[]>();
  for (const root of roots) {
    const week = root.week_start;
    if (!week) {
      (isKpiUnfinished(root.status) ? noDate : doneNoDate).push(root);
    } else if (week < today && isKpiUnfinished(root.status)) {
      overdue.push(root);
    } else {
      const list = weeks.get(week) ?? [];
      list.push(root);
      weeks.set(week, list);
    }
  }

  const monthMap = new Map<string, WeekGroup[]>();
  for (const key of [...weeks.keys()].sort()) {
    const mk = monthKeyOf(key);
    const list = monthMap.get(mk) ?? [];
    list.push({ key, label: weekLabel(key), roots: weeks.get(key)! });
    monthMap.set(mk, list);
  }

  // Pasek postępu pokazuje realizację USTALEŃ — prywatne zapiski nie mają go podbijać
  // ani psuć (decyzja: liczby KPI liczą tylko ustalenia).
  const countSubtree = (root: KpiActionItem): [number, number] => {
    const counts = root.origin === "kpi";
    let total = counts ? 1 : 0;
    let done = counts && !isKpiUnfinished(root.status) ? 1 : 0;
    for (const child of childrenOf.get(root.id) ?? []) {
      const [t, d] = countSubtree(child);
      total += t;
      done += d;
    }
    return [total, done];
  };

  const months: MonthGroup[] = [...monthMap.keys()].sort().map((mk) => {
    const weekGroups = monthMap.get(mk)!;
    let total = 0;
    let done = 0;
    for (const w of weekGroups) {
      for (const root of w.roots) {
        const [t, d] = countSubtree(root);
        total += t;
        done += d;
      }
    }
    return { key: mk, label: monthLabel(mk), weeks: weekGroups, total, done };
  });

  return { overdue, months, noDate, doneNoDate, childrenOf };
}

// ── uprawnienia ──────────────────────────────────────────────────────────────────
/**
 * Lustro CanEditKpiActionItem z backendu: swoje zadania (autor / odpowiedzialny) oraz —
 * dla CEO i dyrektora technicznego — wszystkie. Koordynatora przedsięwzięcia sprawdza
 * już tylko serwer (front nie zna tu listy koordynatorów), więc jemu przycisk będzie
 * ukryty mimo uprawnienia — bezpieczna strona pomyłki.
 */
function useCanEdit() {
  const user = useAuthStore((s) => s.user);
  return (item: KpiActionItem) => {
    if (!user) return false;
    if (user.is_ceo || (user.role === "director" && !!user.is_technical_director)) return true;
    return item.assignee === user.id;
  };
}

/**
 * Dzwoneczek („ping") widzą wyłącznie dyrektor techniczny i zarząd — lustro bramki
 * IsTechnicalDirector z backendu (decyzja usera 12.09.2026). Reszta zespołu nie widzi
 * przycisku: ponaglenie ma być sygnałem z góry, a nie kolejnym sposobem popędzania się
 * nawzajem. Backend i tak pilnuje swojego — to jest tylko zdjęcie przycisku z oczu.
 */
function useCanPing(): boolean {
  return canPingKpi(useAuthStore((s) => s.user));
}

// ── siatka kolumn: jak na tablicy Friday, plus kolumna wieku zadania ─────────────
const GRID = "grid grid-cols-[1fr_120px_110px_110px_180px_80px_120px]";
const CELL_BORDER = "border-r border-gray-200 dark:border-gray-600";

/** Dzień (lokalnie) z „2026-08-12" albo z pełnego znacznika czasu z API. */
function dayOf(value: string): Date {
  // Samą datę Date parsuje jako UTC i w części stref cofa o dzień — stąd parseIso.
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return parseIso(value);
  const d = new Date(value);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function dayDiff(from: string, to: string | Date): number {
  const end = to instanceof Date ? new Date(to.getFullYear(), to.getMonth(), to.getDate()) : dayOf(to);
  return Math.round((end.getTime() - dayOf(from).getTime()) / (24 * 3600 * 1000));
}

/**
 * „Ile to już wisi": dla otwartych wiek zadania w dniach, dla zamkniętych dwie daty —
 * utworzenia i zamknięcia. Anulowane nie mają stempla zamknięcia (backend czyści
 * `done_at` poza statusem „zrobione"), więc pokazują sam wiek — bez zgadywania daty.
 */
function AgeCell({ item }: { item: KpiActionItem }) {
  const closedAt = isKpiUnfinished(item.status) ? null : item.done_at;
  const days = dayDiff(item.created_at, closedAt ?? new Date());
  const span = days <= 0 ? "tego samego dnia" : days === 1 ? "1 dzień" : `${days} dni`;
  const title = closedAt
    ? `Utworzono ${fmtDate(item.created_at)} · zamknięto ${fmtDate(closedAt)} · ${span}`
    : `Utworzono ${fmtDate(item.created_at)} · ${days <= 0 ? "dzisiaj" : `${span} temu`}`;
  return (
    <span className="truncate font-mono text-[11px] text-gray-400 dark:text-gray-500" title={title}>
      {closedAt
        ? `${formatDate(item.created_at)} → ${formatDate(closedAt)}`
        : days <= 0 ? "dziś" : `${days} dni`}
    </span>
  );
}

/** „+2 tyg." / „0 tyg." / „-1 tyg." — ta sama kolorystyka co kolumna Termin we Friday. */
function weekText(weekStart: string | null, today: string): { text: string; cls: string } | null {
  if (!weekStart) return null;
  const weeks = Math.round(
    (parseIso(weekStart).getTime() - parseIso(today).getTime()) / (7 * 24 * 3600 * 1000),
  );
  const text = weeks === 0 ? "0 tyg." : weeks > 0 ? `+${weeks} tyg.` : `${weeks} tyg.`;
  const cls =
    weeks < -1
      ? "text-red-700 font-semibold"
      : weeks === -1
        ? "text-red-500 font-medium"
        : weeks === 0
          ? "text-orange-600 font-semibold"
          : weeks === 1
            ? "text-yellow-600"
            : "text-gray-400 dark:text-gray-500";
  return { text, cls };
}

/**
 * Ustalenie dyrekcji vs zapisek własny. O pochodzeniu decyduje miejsce dodania ALBO ręka
 * autora (isDirectorateKpiItem) — jedno i drugie jest zapisane w chwili dodania i nie da
 * się go potem podmienić, więc plakietka jest wiarygodna, a nie deklaratywna.
 *
 * Ustalenie nosi plakietkę wypełnioną czerwienią, zrobione — tylko obwódkę: domknięta
 * sprawa nie ma krzyczeć głośniej niż ta, która stoi.
 */
function OriginChip({ item, finished }: { item: KpiActionItem; finished: boolean }) {
  const hint = kpiOriginHint(item);
  if (isDirectorateKpiItem(item)) {
    const label = item.origin === "kpi" ? `KPI #${item.review_sequence_no}` : "dyrekcja";
    return (
      <span
        className={`flex-shrink-0 rounded-sm border border-[var(--color-primary)] px-1 font-mono text-[10px] font-semibold ${
          finished
            ? "text-[var(--color-primary)] opacity-60"
            : "bg-[var(--color-primary)] text-white"
        }`}
        title={hint}
      >
        {label}
      </span>
    );
  }
  return (
    <span
      className="flex-shrink-0 rounded-sm border border-gray-200 px-1 font-mono text-[10px] text-gray-400 dark:border-gray-600"
      title={hint}
    >
      własne
    </span>
  );
}

// ── komórki wiersza ──────────────────────────────────────────────────────────────
function StatusCell({ item, connectionId }: { item: KpiActionItem; connectionId: string }) {
  const m = useKpiActionItemMutations(connectionId);
  return (
    <div className="relative w-full">
      <select
        value={item.status}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => m.setStatus.mutate({ id: item.id, status: e.target.value })}
        className={`w-full appearance-none cursor-pointer rounded-none border-0 px-2 py-1 pr-5 text-center text-xs font-semibold ${STATUS_CELL[item.status]}`}
      >
        {KPI_ACTION_ORDER.map((s) => (
          <option key={s} value={s}>{KPI_ACTION_LABELS[s]}</option>
        ))}
      </select>
      <ChevronDown size={9} className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 opacity-60" />
    </div>
  );
}

function PriorityCell({ item, connectionId, canEdit }: { item: KpiActionItem; connectionId: string; canEdit: boolean }) {
  const m = useKpiActionItemMutations(connectionId);
  return (
    <div className="relative w-full">
      <select
        value={item.priority}
        disabled={!canEdit}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => m.update.mutate({ id: item.id, payload: { priority: e.target.value as KpiActionPriority } })}
        className={`w-full appearance-none rounded-none border-0 px-2 py-1 pr-5 text-center text-xs font-semibold ${PRIORITY_COLORS[item.priority]} ${canEdit ? "cursor-pointer" : ""}`}
      >
        {(Object.keys(PRIORITY_LABELS) as KpiActionPriority[]).map((p) => (
          <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>
        ))}
      </select>
      <ChevronDown size={9} className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 opacity-60" />
    </div>
  );
}

function PersonCell({ item }: { item: KpiActionItem }) {
  const { data: users = [] } = useInternalUsers();
  const user = users.find((u) => u.id === item.assignee);
  if (!item.assignee_name) {
    return <span className="text-xs text-gray-300 dark:text-gray-600">—</span>;
  }
  return (
    <span className="flex min-w-0 items-center gap-1" title={item.assignee_name}>
      <UserAvatar
        userId={item.assignee ?? undefined}
        fullName={item.assignee_name}
        profilePictureUrl={user?.profile_picture_url}
        size="xs"
      />
      <span className="truncate text-xs text-gray-500 dark:text-gray-400">{item.assignee_name}</span>
    </span>
  );
}

/** Dyskusja pod konkretnym zadaniem — „dlaczego to stoi" przy samej robocie. */
function ItemComments({ item, connectionId }: { item: KpiActionItem; connectionId: string }) {
  const { data: comments = [], isLoading } = useKpiItemComments(item.id);
  const m = useKpiItemCommentMutations(item.id, connectionId);
  const [draft, setDraft] = useState("");
  const send = () => {
    if (!draft.trim()) return;
    m.create.mutate({ content: draft.trim() }, { onSuccess: () => setDraft("") });
  };
  return (
    <div className="border-b border-gray-100 bg-[var(--color-bg-elevated)]/60 px-4 py-2 dark:border-gray-700/60 dark:bg-blue-900/10">
      {isLoading ? (
        <p className="font-mono text-[11px] text-gray-400">Wczytywanie…</p>
      ) : (
        comments.map((c) => (
          <div key={c.id} className="py-1">
            <p className="font-mono text-[11px] text-gray-400 dark:text-gray-500">
              {c.author_name} · {fmtDate(c.created_at)}{c.is_edited ? " · edytowany" : ""}
            </p>
            <CommentBody content={c.content} mentions={c.mentions ?? []} />
          </div>
        ))
      )}
      <input
        className="mt-1 w-full rounded border border-gray-200 bg-white px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-800"
        placeholder="Komentarz… Enter aby wysłać"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") send(); }}
      />
    </div>
  );
}

/**
 * Dzwoneczek przy zadaniu: jeden klik i koordynatorzy przedsięwzięcia (zawsze) oraz
 * odpowiedzialny dostają powiadomienie w ELGW 5 i mail z aron@ podpisany tym, kto kliknął.
 *
 * ŚWIADOMIE bez blokady po kliknięciu i bez odstępu między pingami — „człowiek decyduje
 * co i ile klika". Licznik obok jest śladem (kto i ile razy ponaglał), nie hamulcem.
 */
function PingButton({ item, connectionId }: { item: KpiActionItem; connectionId: string }) {
  const m = useKpiActionItemMutations(connectionId);
  const canPing = useCanPing();
  if (!canPing) {
    return item.ping_count > 0 ? (
      <span
        className="flex-shrink-0 font-mono text-[10px] text-[var(--color-primary)]"
        title={pingHint(item, false)}
      >
        {item.ping_count}×
      </span>
    ) : null;
  }
  const pending = m.ping.isPending && m.ping.variables === item.id;
  return (
    <span className="flex flex-shrink-0 items-center gap-0.5">
      <button
        type="button"
        disabled={pending}
        title={pingHint(item, true)}
        onClick={(e) => {
          e.stopPropagation();
          m.ping.mutate(item.id, {
            onError: () => alert("Nie udało się wysłać ponaglenia. Spróbuj ponownie."),
          });
        }}
        className={`rounded p-0.5 transition-colors ${
          item.ping_count > 0
            ? "text-[var(--color-primary)]"
            : "text-gray-300 hover:text-[var(--color-primary)] dark:text-gray-600"
        } ${pending ? "opacity-40" : "hover:bg-red-50 dark:hover:bg-red-900/30"}`}
      >
        <Bell size={12} />
      </button>
      {item.ping_count > 0 && (
        <span className="font-mono text-[10px] text-[var(--color-primary)]">{item.ping_count}×</span>
      )}
    </span>
  );
}

/**
 * Podpowiedź dzwoneczka. Dwie różne — kto może ponaglać, czyta o skutkach kliknięcia;
 * kto nie może, czyta FAKT („zadanie ponaglano"), bo opis akcji, której nie da się zrobić,
 * jest tylko hałasem. W obu ponaglenie ma nazwisko: nie jest anonimowe.
 */
function pingHint(item: KpiActionItem, canPing: boolean): string {
  const last = item.last_ping
    ? `Ostatnio: ${item.last_ping.by_name}, ${fmtDateTime(item.last_ping.at)}`
    : "";
  if (canPing) {
    const what = "Ponaglenie: powiadomienie i mail do koordynatorów i odpowiedzialnego";
    return last ? `${what}\n${last}` : what;
  }
  const times = item.ping_count === 1 ? "raz" : `${item.ping_count}×`;
  return last ? `Zadanie ponaglano ${times}\n${last}` : `Zadanie ponaglano ${times}`;
}

// ── wiersz zadania ───────────────────────────────────────────────────────────────
type DropMode = "before" | "after" | "inside";

interface Dnd {
  draggingId: string | null;
  over: { id: string; mode: DropMode } | null;
  onDragStart: (id: string) => void;
  onDragOverRow: (e: React.DragEvent, id: string) => void;
  onDropRow: (e: React.DragEvent, item: KpiActionItem) => void;
  onDragEnd: () => void;
}

function TaskRow({
  item,
  depth,
  connectionId,
  childrenOf,
  dnd,
  defaultAssignee,
  today,
  onOpen,
}: {
  item: KpiActionItem;
  depth: number;
  connectionId: string;
  childrenOf: Map<string, KpiActionItem[]>;
  dnd: Dnd;
  defaultAssignee: string;
  today: string;
  onOpen: (id: string) => void;
}) {
  const m = useKpiActionItemMutations(connectionId);
  const canEdit = useCanEdit()(item);
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const children = childrenOf.get(item.id) ?? [];
  const finished = !isKpiUnfinished(item.status);
  const over = dnd.over?.id === item.id ? dnd.over.mode : null;
  const wk = weekText(item.week_start, today);
  // Ustalenie dyrekcji na czerwono — plakietki 10 px nikt nie czytał przy czterdziestu
  // wierszach, więc sygnał niesie sam tekst zadania. Odhaczone gaśnie do przygaszonej
  // czerwieni, żeby lista zrobionych nie krzyczała głośniej niż to, co stoi.
  const directive = isDirectorateKpiItem(item);
  const descClass = finished
    ? directive
      ? "text-[rgba(220,31,31,0.55)] line-through"
      : "text-gray-400 line-through dark:text-gray-500"
    : directive
      ? "font-semibold text-[var(--color-primary)]"
      : "text-[var(--color-text-primary)]";

  return (
    <>
      {over === "before" && <div className="h-0.5 bg-[var(--color-primary)]" />}
      {editing ? (
        <EditRow item={item} connectionId={connectionId} depth={depth} onDone={() => setEditing(false)} />
      ) : (
        <div
          draggable
          onDragStart={() => dnd.onDragStart(item.id)}
          onDragOver={(e) => dnd.onDragOverRow(e, item.id)}
          onDrop={(e) => dnd.onDropRow(e, item)}
          onDragEnd={dnd.onDragEnd}
          className={`${GRID} group/strow items-center border-b border-gray-100 transition-colors hover:bg-[var(--color-primary-dim)]/60 dark:border-gray-700/60 dark:hover:bg-blue-900/10 ${
            over === "inside" ? "ring-1 ring-inset ring-[var(--color-primary)]" : ""
          } ${dnd.draggingId === item.id ? "opacity-40" : ""}`}
        >
          {/* Zadanie */}
          <div
            className={`flex items-center gap-1 truncate py-1 ${CELL_BORDER}`}
            style={{ paddingLeft: `${8 + depth * 20}px` }}
          >
            <GripVertical
              size={12}
              className="flex-shrink-0 cursor-grab text-gray-300 opacity-0 transition-opacity group-hover/strow:opacity-100 dark:text-gray-600"
            />
            {children.length > 0 ? (
              <button
                onClick={() => setCollapsed((v) => !v)}
                className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:text-gray-500"
                title={collapsed ? "Rozwiń podzadania" : "Zwiń podzadania"}
              >
                {collapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
              </button>
            ) : depth > 0 ? (
              <span className="flex-shrink-0 select-none font-mono text-xs leading-none text-gray-300 dark:text-gray-600">└─</span>
            ) : (
              <span className="w-3 flex-shrink-0" />
            )}
            <button
              type="button"
              onClick={() => m.setStatus.mutate({ id: item.id, status: finished ? "open" : "done" })}
              className={`flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center rounded-sm border ${
                finished ? "border-emerald-600 bg-emerald-600 text-white" : "border-gray-300 hover:border-emerald-600 dark:border-gray-600"
              }`}
              title={finished ? "Cofnij do zrobienia" : "Oznacz jako zrobione"}
            >
              {finished && <Check size={9} />}
            </button>
            <button
              type="button"
              className={`truncate text-left text-sm hover:text-[var(--color-primary)] ${descClass}`}
              title={`${kpiOriginHint(item)}\nKliknij, aby otworzyć szczegóły`}
              onClick={() => onOpen(item.id)}
              onDoubleClick={() => canEdit && setEditing(true)}
            >
              {item.description}
            </button>
            {children.length > 0 && (
              <span className="flex-shrink-0 font-mono text-[11px] text-gray-400">({children.length})</span>
            )}
            <OriginChip item={item} finished={finished} />
            <PingButton item={item} connectionId={connectionId} />
            <span className="ml-auto mr-1 flex flex-shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover/strow:opacity-100">
              <button
                onClick={() => setAdding(true)}
                title="Dodaj podzadanie"
                className="rounded p-0.5 text-gray-400 hover:bg-blue-100 hover:text-[var(--color-primary)] dark:hover:bg-blue-900/40"
              >
                <Plus size={12} />
              </button>
              {canEdit && (
                <button
                  onClick={() => setEditing(true)}
                  title="Edytuj zadanie"
                  className="rounded p-0.5 text-gray-400 hover:bg-blue-100 hover:text-[var(--color-primary)] dark:hover:bg-blue-900/40"
                >
                  <Pencil size={12} />
                </button>
              )}
              {canEdit && (
                <button
                  onClick={() => { if (confirm("Usunąć zadanie razem z podzadaniami?")) m.remove.mutate(item.id); }}
                  title="Usuń zadanie"
                  className="rounded p-0.5 text-gray-400 hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/40"
                >
                  <Trash2 size={12} />
                </button>
              )}
            </span>
          </div>

          <div className={`flex ${CELL_BORDER}`}><StatusCell item={item} connectionId={connectionId} /></div>
          <div className={`flex ${CELL_BORDER}`}><PriorityCell item={item} connectionId={connectionId} canEdit={canEdit} /></div>
          <div className={`flex justify-center px-2 py-1 ${CELL_BORDER}`}>
            {!finished && wk ? (
              <span className={`text-xs font-medium ${wk.cls}`}>{wk.text}</span>
            ) : (
              <span className="text-xs text-gray-300 dark:text-gray-600">—</span>
            )}
          </div>
          <div className={`flex justify-center px-2 py-1 ${CELL_BORDER}`}>
            <AgeCell item={item} />
          </div>
          <button
            onClick={() => setShowComments((v) => !v)}
            className={`flex justify-center px-2 py-1 ${CELL_BORDER} hover:text-[var(--color-primary)]`}
            title="Komentarze do zadania"
          >
            {item.comments_count > 0 ? (
              <span className="flex items-center gap-0.5 text-xs text-gray-400 dark:text-gray-500">
                <MessageSquare size={11} />
                {item.comments_count}
              </span>
            ) : (
              <span className="text-xs text-gray-200 dark:text-gray-600">—</span>
            )}
          </button>
          <div className="flex min-w-0 justify-start px-2 py-1"><PersonCell item={item} /></div>
        </div>
      )}
      {over === "after" && <div className="h-0.5 bg-[var(--color-primary)]" />}

      {showComments && <ItemComments item={item} connectionId={connectionId} />}

      {!collapsed && children.map((child) => (
        <TaskRow
          key={child.id}
          item={child}
          depth={depth + 1}
          connectionId={connectionId}
          childrenOf={childrenOf}
          dnd={dnd}
          defaultAssignee={defaultAssignee}
          today={today}
          onOpen={onOpen}
        />
      ))}
      {adding && (
        <AddRow
          connectionId={connectionId}
          parentId={item.id}
          weekStart={item.week_start}
          defaultAssignee={defaultAssignee}
          depth={depth + 1}
          onDone={() => setAdding(false)}
        />
      )}
    </>
  );
}

function EditRow({
  item,
  connectionId,
  depth,
  onDone,
}: {
  item: KpiActionItem;
  connectionId: string;
  depth: number;
  onDone: () => void;
}) {
  const m = useKpiActionItemMutations(connectionId);
  const { data: users = [] } = useInternalUsers();
  const [description, setDescription] = useState(item.description);
  const [assignee, setAssignee] = useState(item.assignee ?? "");
  const [week, setWeek] = useState<string | null>(item.week_start);
  const userOptions = useMemo(
    () => [...users].sort((a, b) => a.full_name.localeCompare(b.full_name, "pl"))
      .map((u) => ({ value: u.id, label: u.full_name })),
    [users],
  );
  const save = () => {
    if (!description.trim()) return;
    m.update.mutate(
      { id: item.id, payload: { description: description.trim(), assignee: assignee || null, due_date: week } },
      { onSuccess: onDone },
    );
  };
  return (
    <div
      className="flex flex-wrap items-center gap-2 border-b border-gray-100 bg-[var(--color-bg-elevated)]/60 px-4 py-2 dark:border-gray-700/60 dark:bg-blue-900/10"
      style={{ paddingLeft: `${12 + depth * 20}px` }}
    >
      <input
        className="min-w-[12rem] flex-1 rounded border border-blue-300 bg-white px-2 py-1 text-sm dark:bg-gray-800"
        value={description}
        autoFocus
        onChange={(e) => setDescription(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") save();
          if (e.key === "Escape") onDone();
        }}
      />
      <div className="w-44">
        <SearchableSelect
          options={userOptions}
          value={assignee}
          placeholder="— odpowiedzialny —"
          onChange={(e) => setAssignee(e.target.value)}
        />
      </div>
      <WeekSelect value={week} onChange={setWeek} placeholder="— do następnego KPI —" />
      <Button size="sm" disabled={!description.trim() || m.update.isPending} onClick={save}>Zapisz</Button>
      <Button size="sm" variant="ghost" onClick={onDone}>Anuluj</Button>
    </div>
  );
}

/**
 * Jedno pole + Enter, w linii tabeli — nie osobne pudełko z czerwonym przyciskiem.
 * Świadomie NIE pyta o spotkanie KPI: wysyła `connection`, a backend podpina zadanie pod
 * bieżący (albo świeżo otwarty) przegląd. Domyślnie przypisuje do siebie.
 */
function AddRow({
  connectionId,
  parentId,
  weekStart,
  defaultAssignee,
  depth = 0,
  onDone,
}: {
  connectionId: string;
  parentId?: string | null;
  weekStart: string | null;
  defaultAssignee: string;
  depth?: number;
  onDone: () => void;
}) {
  const m = useKpiActionItemMutations(connectionId);
  const [description, setDescription] = useState("");
  const add = () => {
    const text = description.trim();
    if (!text) {
      onDone();
      return;
    }
    m.create.mutate(
      {
        connection: connectionId,
        parent: parentId ?? null,
        description: text,
        assignee: defaultAssignee || null,
        due_date: weekStart,
      },
      { onSuccess: () => { setDescription(""); onDone(); } },
    );
  };
  return (
    <div
      className="border-b border-gray-100 bg-[var(--color-bg-elevated)]/60 py-1.5 pr-4 dark:border-gray-700/60 dark:bg-blue-900/10"
      style={{ paddingLeft: `${20 + depth * 20}px` }}
    >
      <input
        autoFocus
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") add();
          if (e.key === "Escape") onDone();
        }}
        onBlur={() => { if (!description.trim()) onDone(); }}
        disabled={m.create.isPending}
        placeholder="Treść zadania… Enter aby zapisać, Esc aby anulować"
        className="w-full rounded border border-blue-300 bg-white px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] dark:bg-gray-800"
      />
    </div>
  );
}

/** „＋ zadanie" — dyskretny wiersz na końcu tygodnia, zamienia się w pole po kliknięciu. */
function AddTrigger({ label, depth = 0, onClick }: { label: string; depth?: number; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-1 border-b border-gray-100 py-1 text-left text-xs text-gray-400 hover:bg-[var(--color-primary-dim)]/40 hover:text-[var(--color-primary)] dark:border-gray-700/60 dark:text-gray-500"
      style={{ paddingLeft: `${20 + depth * 20}px` }}
    >
      <Plus size={12} />
      {label}
    </button>
  );
}

// ── nagłówki grup (miesiąc / tydzień / kubełki stałe) ────────────────────────────
function GroupRow({
  label,
  meta,
  collapsed,
  onToggle,
  level,
  tone = "normal",
  dropProps,
  dropActive,
  onAdd,
}: {
  label: string;
  meta?: React.ReactNode;
  collapsed: boolean;
  onToggle: () => void;
  level: "month" | "week";
  tone?: "normal" | "danger" | "muted";
  dropProps?: {
    onDragOver: (e: React.DragEvent) => void;
    onDragLeave: () => void;
    onDrop: (e: React.DragEvent) => void;
  };
  dropActive?: boolean;
  onAdd?: () => void;
}) {
  const toneClass =
    tone === "danger"
      ? "text-red-600 dark:text-red-400"
      : tone === "muted"
        ? "text-gray-400 dark:text-gray-500"
        : "text-[var(--color-text-secondary)]";
  return (
    <div
      {...dropProps}
      className={`group/grp flex items-center gap-2 border-b py-1.5 pr-3 ${
        level === "month"
          ? "border-gray-200 bg-gray-100/80 dark:border-gray-600 dark:bg-gray-800/70"
          : "border-gray-100 bg-gray-50/70 dark:border-gray-700/60 dark:bg-gray-800/40"
      } ${dropActive ? "ring-1 ring-inset ring-[var(--color-primary)]" : ""}`}
      style={{ paddingLeft: level === "month" ? 8 : 20 }}
    >
      <button
        type="button"
        onClick={onToggle}
        className={`flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide ${toneClass}`}
      >
        {collapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
        {label}
      </button>
      {onAdd && (
        <button
          type="button"
          onClick={onAdd}
          title="Dodaj zadanie w tym tygodniu"
          className="rounded p-0.5 text-gray-400 opacity-0 transition-opacity hover:bg-blue-100 hover:text-[var(--color-primary)] group-hover/grp:opacity-100 dark:hover:bg-blue-900/40"
        >
          <Plus size={12} />
        </button>
      )}
      {meta && <span className="ml-auto font-mono text-[11px] text-gray-400 dark:text-gray-500">{meta}</span>}
    </div>
  );
}

function ProgressBar({ done, total }: { done: number; total: number }) {
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-block h-1.5 w-16 overflow-hidden rounded-full bg-gray-200 align-middle dark:bg-gray-600">
        <span className="block h-full bg-emerald-500" style={{ width: `${pct}%` }} />
      </span>
      {done}/{total}
    </span>
  );
}

// ── całość ───────────────────────────────────────────────────────────────────────
export function KpiBacklogTree({
  items,
  connectionId,
  coordinators,
}: {
  items: KpiActionItem[];
  connectionId: string;
  coordinators: string[];
}) {
  const m = useKpiActionItemMutations(connectionId);
  const { data: users = [] } = useInternalUsers();
  const currentUserId = useAuthStore((s) => s.user?.id);
  const { collapsed, toggle } = useKpiGroupStates(connectionId);
  const today = currentMonday();
  const buckets = useMemo(() => buildBuckets(items, today), [items, today]);
  const [addingIn, setAddingIn] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const openItem = openId ? items.find((i) => i.id === openId) ?? null : null;
  // Deep link z powiadomienia („oznaczono Cię w komentarzu zadania"): ?item=<uuid>
  // otwiera panel tego zadania. Parametr zdejmujemy po otwarciu, żeby zamknięcie
  // panelu nie wracało do niego przy kolejnym renderze.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepItemId = searchParams.get("item");
  useEffect(() => {
    if (!deepItemId) return;
    setOpenId(deepItemId);
    const next = new URLSearchParams(searchParams);
    next.delete("item");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepItemId]);

  // Domyślny odpowiedzialny nowego zadania: ja, jeśli jestem na liście; inaczej pierwszy
  // koordynator. Nikt nic nie wybiera przy dopisywaniu.
  const defaultAssignee = useMemo(() => {
    if (currentUserId && users.some((u) => u.id === currentUserId)) return currentUserId;
    return coordinators.filter((id) => users.some((u) => u.id === id))[0] ?? "";
  }, [coordinators, users, currentUserId]);

  const dragId = useRef<string | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<{ id: string; mode: DropMode } | null>(null);
  const [overGroup, setOverGroup] = useState<string | null>(null);

  const siblingsOf = (item: KpiActionItem): KpiActionItem[] =>
    item.parent
      ? buckets.childrenOf.get(item.parent) ?? []
      : items.filter((i) => !i.parent && i.week_start === item.week_start);

  function reset() {
    dragId.current = null;
    setDragging(null);
    setOver(null);
    setOverGroup(null);
  }

  const dnd: Dnd = {
    draggingId: dragging,
    over,
    onDragStart(id) {
      dragId.current = id;
      setDragging(id);
    },
    onDragOverRow(e, id) {
      e.preventDefault();
      e.stopPropagation();
      if (!dragId.current || dragId.current === id) return;
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      const pct = (e.clientY - rect.top) / rect.height;
      // Górne/dolne 25 % = zmiana kolejności, środek = zagnieżdżenie. Jedno przeciągnięcie
      // obsługuje oba ruchy, tak jak na tablicy Friday.
      setOver({ id, mode: pct < 0.25 ? "before" : pct > 0.75 ? "after" : "inside" });
      setOverGroup(null);
    },
    onDropRow(e, target) {
      e.preventDefault();
      e.stopPropagation();
      const sourceId = dragId.current;
      const mode = over?.mode ?? "inside";
      reset();
      if (!sourceId || sourceId === target.id) return;
      const source = items.find((i) => i.id === sourceId);
      if (!source) return;

      if (mode === "inside") {
        m.reparent.mutate({ id: sourceId, parent: target.id });
        return;
      }
      const applyOrder = () => {
        const ids = siblingsOf(target).map((i) => i.id).filter((id) => id !== sourceId);
        const at = ids.indexOf(target.id);
        if (at === -1) return;
        ids.splice(mode === "after" ? at + 1 : at, 0, sourceId);
        m.reorder.mutate(ids);
      };
      if (source.parent === target.parent && source.week_start === target.week_start) {
        applyOrder();
      } else {
        m.reparent.mutate(
          { id: sourceId, parent: target.parent, due_date: target.week_start },
          { onSuccess: applyOrder },
        );
      }
    },
    onDragEnd: () => reset(),
  };

  /** Upuszczenie na nagłówek tygodnia = zmiana terminu i wyjście na górny poziom. */
  const groupDrop = (key: string, weekStart: string | null) => ({
    dropActive: overGroup === key,
    dropProps: {
      onDragOver: (e: React.DragEvent) => {
        if (!dragId.current) return;
        e.preventDefault();
        setOver(null);
        setOverGroup(key);
      },
      onDragLeave: () => setOverGroup((k) => (k === key ? null : k)),
      onDrop: (e: React.DragEvent) => {
        e.preventDefault();
        const sourceId = dragId.current;
        reset();
        if (sourceId) m.reparent.mutate({ id: sourceId, parent: null, due_date: weekStart });
      },
    },
  });

  const isOpen = (key: string) => !collapsed.has(key);
  const flip = (key: string) => toggle.mutate({ groupKey: key, collapsed: !collapsed.has(key) });

  const rows = (roots: KpiActionItem[]) =>
    roots.map((root) => (
      <TaskRow
        key={root.id}
        item={root}
        depth={0}
        connectionId={connectionId}
        childrenOf={buckets.childrenOf}
        dnd={dnd}
        defaultAssignee={defaultAssignee}
        today={today}
        onOpen={setOpenId}
      />
    ));

  const hasCurrentWeek = buckets.months.some((mo) => mo.weeks.some((w) => w.key === today));

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[840px]">
        {/* Nagłówki kolumn — te same co na tablicy Friday */}
        <div className={`${GRID} rounded-xl border-2 border-gray-200 bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-400 dark:border-gray-600 dark:bg-gray-800/50 dark:text-gray-500`}>
          <div className={`px-4 py-2 border-r-2 border-gray-200 dark:border-gray-600`}>Zadanie</div>
          <div className={`px-2 py-2 text-center border-r-2 border-gray-200 dark:border-gray-600`}>Status</div>
          <div className={`px-2 py-2 text-center border-r-2 border-gray-200 dark:border-gray-600`}>Priorytet</div>
          <div className={`px-2 py-2 text-center border-r-2 border-gray-200 dark:border-gray-600`}>Tydzień</div>
          <div className={`px-2 py-2 text-center border-r-2 border-gray-200 dark:border-gray-600`} title="Wiek zadania; przy zamkniętych data utworzenia → data zamknięcia">Utworzono</div>
          <div className={`px-2 py-2 text-center border-r-2 border-gray-200 dark:border-gray-600`}>Kom.</div>
          <div className="px-3 py-2">Osoba</div>
        </div>

        <div className="mt-2 overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
          {/* Zaległe — jedyny kubełek wymagający decyzji dzisiaj, więc na samej górze. */}
          {buckets.overdue.length > 0 && (
            <>
              <GroupRow
                label={`Po terminie (${buckets.overdue.length})`}
                collapsed={!isOpen("overdue")}
                onToggle={() => flip("overdue")}
                level="month"
                tone="danger"
                {...groupDrop("overdue", today)}
              />
              {isOpen("overdue") && rows(buckets.overdue)}
            </>
          )}

          {buckets.months.map((month) => (
            <div key={month.key}>
              <GroupRow
                label={month.label}
                meta={<ProgressBar done={month.done} total={month.total} />}
                collapsed={!isOpen(month.key)}
                onToggle={() => flip(month.key)}
                level="month"
              />
              {isOpen(month.key) && month.weeks.map((week) => (
                <div key={week.key}>
                  <GroupRow
                    label={week.key === today ? `${week.label} · ten tydzień` : week.label}
                    meta={`${week.roots.length} zad.`}
                    collapsed={!isOpen(week.key)}
                    onToggle={() => flip(week.key)}
                    level="week"
                    tone={week.key === today ? "normal" : "muted"}
                    onAdd={() => setAddingIn(week.key)}
                    {...groupDrop(week.key, week.key)}
                  />
                  {isOpen(week.key) && (
                    <>
                      {rows(week.roots)}
                      {addingIn === week.key ? (
                        <AddRow
                          connectionId={connectionId}
                          weekStart={week.key}
                          defaultAssignee={defaultAssignee}
                          onDone={() => setAddingIn(null)}
                        />
                      ) : (
                        <AddTrigger label="zadanie w tym tygodniu" onClick={() => setAddingIn(week.key)} />
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          ))}

          {/* Bieżący tydzień zawsze widoczny, nawet pusty — inaczej nie ma gdzie dopisać. */}
          {!hasCurrentWeek && (
            <>
              <GroupRow
                label={`${weekLabel(today)} · ten tydzień`}
                meta="0 zad."
                collapsed={false}
                onToggle={() => {}}
                level="week"
                onAdd={() => setAddingIn(today)}
                {...groupDrop(today, today)}
              />
              {addingIn === today ? (
                <AddRow
                  connectionId={connectionId}
                  weekStart={today}
                  defaultAssignee={defaultAssignee}
                  onDone={() => setAddingIn(null)}
                />
              ) : (
                <AddTrigger label="zadanie w tym tygodniu" onClick={() => setAddingIn(today)} />
              )}
            </>
          )}

          <GroupRow
            label={`Bez terminu — do następnego KPI (${buckets.noDate.length})`}
            collapsed={!isOpen("no_date")}
            onToggle={() => flip("no_date")}
            level="week"
            tone="muted"
            onAdd={() => setAddingIn("no_date")}
            {...groupDrop("no_date", null)}
          />
          {isOpen("no_date") && (
            <>
              {rows(buckets.noDate)}
              {addingIn === "no_date" && (
                <AddRow
                  connectionId={connectionId}
                  weekStart={null}
                  defaultAssignee={defaultAssignee}
                  onDone={() => setAddingIn(null)}
                />
              )}
            </>
          )}

          {buckets.doneNoDate.length > 0 && (
            <>
              <GroupRow
                label={`Zrobione bez terminu (${buckets.doneNoDate.length})`}
                collapsed={!isOpen("done")}
                onToggle={() => flip("done")}
                level="week"
                tone="muted"
              />
              {isOpen("done") && rows(buckets.doneNoDate)}
            </>
          )}
        </div>
      </div>

      {openItem && (
        <KpiItemDetailPanel
          item={openItem}
          items={items}
          connectionId={connectionId}
          onClose={() => setOpenId(null)}
          onOpenOther={setOpenId}
        />
      )}
    </div>
  );
}
